import { StateCreator } from 'zustand'
import { SortOrder } from '@moonpig/web-explore-types-graphql'
import { ApplicableFilter, FilterItem } from '../../../services/types/services'
import { FilterAction } from '../../../types'
import {
  getPersistedFilters,
  getFiltersById,
  updateSelectedFilters,
  optimisticUpdatedFilterMapping,
  updateCategories,
  clearCategoryFilters,
  updateRoute,
} from '../../helpers'
import { FiltersState } from './types'
import { ContextState } from '../context'
import { ProductsState } from '../products'
import { SharedState } from '../shared'
import { applySiblingFacetSelection } from '../../helpers/applySiblingFacetSelection'

export const createFiltersSlice: StateCreator<
  FiltersState &
    Partial<ContextState> &
    Partial<SharedState> &
    Partial<ProductsState>
> = (set, get) => ({
  filtersMenuOpen: false,
  filterSelectionError: false,
  filters: [],
  preAppliedFilters: [],
  rangeFilters: [],
  selectedFilters: [],
  filteringEvent: undefined,
  selectedCategories: [],
  queryFilters: null,
  initialSortValue: SortOrder.POPULARITY,
  sortValue: SortOrder.POPULARITY,
  sortMenuOpen: false,
  optimisticRender: false,
  hasNbaFilters: false,
  hasRudeProducts: false,
  filterActionQueue: [],
  toggleFiltersMenu: source => {
    set(state => ({
      filtersMenuOpen: !state.filtersMenuOpen,
      selectedCategories: [],
      sortMenuOpen: source === 'sort',
    }))
  },
  toggleFiltersCategory: filtersCategory =>
    set(state => {
      const currentSelectedCategories = [...state.selectedCategories]

      const isCurrentCategory =
        filtersCategory.id ===
        currentSelectedCategories[currentSelectedCategories.length - 1]?.id
      if (isCurrentCategory) {
        currentSelectedCategories.pop()
      } else {
        currentSelectedCategories.push(filtersCategory)
      }
      return {
        selectedCategories: currentSelectedCategories,
      }
    }),
  optimisticallyApplyFilters: async filterActions => {
    const { selectedFilters, selectedCategories, filters } = get()

    let updatedSelectedFilters = selectedFilters
    let optimisticUpdatedFilters = filters

    filterActions.forEach(({ filter, select }) => {
      updatedSelectedFilters = updateSelectedFilters({
        selectedFilters: updatedSelectedFilters,
        newFilter: filter,
        select,
        allFilters: filters,
      })

      optimisticUpdatedFilters = optimisticUpdatedFilters.map(f =>
        optimisticUpdatedFilterMapping(f, filter, select),
      )
    })

    const updatedCategories = updateCategories(
      optimisticUpdatedFilters,
      selectedCategories.map(c => c.id),
    )

    set(() => {
      return {
        selectedFilters: updatedSelectedFilters,
        filters: optimisticUpdatedFilters,
        selectedCategories: updatedCategories,
        optimisticRender: true,
      }
    })
  },
  toggleFilter: async ({ filter, select, source }) => {
    const {
      selectedFilters,
      queryFilters,
      toggleLoading,
      preAppliedFilters,
      selectedCategories,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      optimisticallyApplyFilters,
      filters,
      filterActionQueue,
      searchContext,
      queryProducts,
      setFilterError,
      clearFilterError,
    } = get()
    /* istanbul ignore else */
    if (queryFilters) {
      try {
        toggleLoading?.()
        clearFilterError()

        const newfilterAction = { filter, select, source }
        set(() => {
          return {
            filterActionQueue: [...filterActionQueue, newfilterAction],
          }
        })
        const filterActions = get().filterActionQueue

        const shouldOptimisticallyApplyFilters = filterActions.length <= 1

        if (shouldOptimisticallyApplyFilters) {
          optimisticallyApplyFilters(filterActions)
        }
        let filteredPreAppliedFilters = preAppliedFilters

        if (!select) {
          filteredPreAppliedFilters = preAppliedFilters.filter(
            x => x.key !== filter.directParentKey && x.key !== filter.id,
          )
        }

        const selectedFiltersWithPreappliedFilters =
          preAppliedFilters.length > 0
            ? applySiblingFacetSelection({
                selectedFilters,
                preAppliedFilters,
                filters,
                filter,
                select,
              })
            : selectedFilters

        const updatedSelectedFilters = filterActions.reduce(
          // eslint-disable-next-line @typescript-eslint/no-shadow
          (currentFilters, { filter, select }) => {
            return updateSelectedFilters({
              selectedFilters: currentFilters,
              newFilter: filter,
              select,
              allFilters: filters,
            })
          },
          selectedFiltersWithPreappliedFilters,
        )

        const updatedFilters = await queryFilters([
          ...updatedSelectedFilters.map(f => ({
            key: f.id,
            group: f.parent,
            userApplied: true,
          })),
          ...filteredPreAppliedFilters,
        ])

        const updatedCategories = updateCategories(
          updatedFilters,
          selectedCategories.map(c => c.id),
        )

        const removedSuggestion = preAppliedFilters.find(
          f =>
            (f.key === filter.id || f.key === filter.directParentKey) &&
            f.suggestion === true,
        )

        const hasSuggestionBeenRemoved =
          removedSuggestion &&
          removedSuggestion?.key === searchContext?.selectedSuggestion?.facetKey

        updateRoute({
          router,
          selectedFilters: updatedSelectedFilters,
          preAppliedFilters: filteredPreAppliedFilters,
          mostRecentSearchTerm: hasSuggestionBeenRemoved
            ? searchContext?.selectedSuggestion?.label ||
              /* istanbul ignore next */ searchContext?.searchTerm
            : '',
        })

        if (hasSuggestionBeenRemoved) {
          toggleLoading?.(false)
          return
        }

        const latestFilterActions = get().filterActionQueue

        const filteringEvent = {
          action: select
            ? FilterAction.Add
            : /* istanbul ignore next */ FilterAction.Remove,
          source,
          filter,
        }

        await queryProducts?.({
          newPage: 1,
          isLoadMore: false,
          isPrevious: false,
          filters: updatedSelectedFilters,
          filteringEvent,
          allFilters: updatedFilters,
        })

        toggleLoading?.(false)

        set(() => {
          return {
            filters: updatedFilters,
            selectedFilters: updatedSelectedFilters,
            select,
            filteringEvent,
            selectedCategories: updatedCategories,
            preAppliedFilters: filteredPreAppliedFilters,
            optimisticRender: false,

            filterActionQueue: latestFilterActions.filter(
              latestAction =>
                !filterActions.some(
                  action =>
                    latestAction.filter === action.filter &&
                    latestAction.select === action.select &&
                    latestAction.source === action.source,
                ),
            ),
          }
        })
      } catch {
        toggleLoading?.(false)
        setFilterError()
        set(() => {
          return {
            selectedFilters,
            selectedCategories,
            filters,
            optimisticRender: false,
          }
        })
      }
    }
  },
  applyRangeFilter: async ({ filter, filterId, value, source }) => {
    // eslint-disable-next-line no-console
    console.log('applyRangeFilter', { filter, filterId, value, source })
  },
  onRangeFilterChange: ({ filterId, value }) => {
    const { rangeFilters } = get()

    const updatedRangeFilters = rangeFilters.map(f =>
      f.name === filterId ? { ...f, value } : /* istanbul ignore next */ f,
    )

    set(() => ({
      rangeFilters: updatedRangeFilters,
    }))
  },
  clearFilters: async ({ category, type, source }) => {
    const {
      queryFilters,
      toggleLoading,
      selectedFilters,
      pageContext: /* istanbul ignore next */ { router } = { router: null },
      filters,
      queryProducts,
      searchContext,
      initialSortValue,
      preAppliedFilters,
    } = get()
    let updatedFilters: FilterItem[] | null = null
    let updatedSelectedFilters: ApplicableFilter[] | null = null

    const filteringEvent = {
      action: category ? FilterAction.Clear : FilterAction.ClearAll,
      filter: category,
      source,
    }
    const preAppliedFiltersWithSuggestionsRemoved = preAppliedFilters.filter(
      f => !f.suggestion,
    )

    /* istanbul ignore else */
    if (queryFilters) {
      if (type !== 'sort') {
        toggleLoading?.()
        const persistedFilters = getFiltersById({
          filterIds: getPersistedFilters(),
          allFilters: filters,
        })
        updatedSelectedFilters = category
          ? clearCategoryFilters(category, [...selectedFilters])
          : persistedFilters

        updatedFilters = await queryFilters([
          ...preAppliedFiltersWithSuggestionsRemoved,
          ...updatedSelectedFilters.map(f => ({
            key: f.id,
            group: f.parent,
            userApplied: true,
          })),
        ])

        const hasSuggestionBeenRemoved =
          preAppliedFilters.length !==
          preAppliedFiltersWithSuggestionsRemoved.length

        updateRoute({
          router,
          selectedFilters: updatedSelectedFilters,
          preAppliedFilters: [],
          mostRecentSearchTerm:
            searchContext?.selectedSuggestion?.label ||
            searchContext?.searchTerm ||
            '',
        })

        if (hasSuggestionBeenRemoved) {
          toggleLoading?.(false)
          return
        }

        await queryProducts?.({
          newPage: 1,
          isLoadMore: false,
          isPrevious: false,
          filters: updatedSelectedFilters,
          filteringEvent,
        })

        toggleLoading?.(false)
      } else {
        await queryProducts?.({
          newPage: 1,
          isLoadMore: false,
          isPrevious: false,
          sortOrder: initialSortValue,
          filteringEvent,
        })

        toggleLoading?.(false)
      }

      set(state => {
        return {
          filters: updatedFilters || filters,
          selectedFilters: updatedSelectedFilters || selectedFilters,
          filteringEvent: {
            action: category ? FilterAction.Clear : FilterAction.ClearAll,
            filter: category,
            source,
          },
          preAppliedFilters: preAppliedFiltersWithSuggestionsRemoved,
          selectedCategories: updatedFilters
            ? updateCategories(
                updatedFilters,
                state.selectedCategories.map(c => c.id),
              )
            : state.selectedCategories,
          sortValue: category ? state.sortValue : state.initialSortValue,
        }
      })
    }
  },
  setFilterError: () => set(() => ({ filterSelectionError: true })),
  clearFilterError: () => set(() => ({ filterSelectionError: false })),
  sortBy: async value => {
    const { queryProducts, toggleLoading } = get()

    toggleLoading?.()
    await queryProducts?.({
      newPage: 1,
      isLoadMore: false,
      isPrevious: false,
      sortOrder: value,
    })

    toggleLoading?.(false)
    set(() => ({ sortValue: value }))
  },
  resetFilteringEvent: () => set(() => ({ filteringEvent: undefined })),
})
